import {
  baseDestroyMapbox,
  baseInitMapbox,
  baseMapContextState,
  baseMapDestroy,
  baseMapInit,
} from './shared-map-context'
import type { MapComponentConfig } from '@/utils/mapbox/mapbox-component-config'
import type {
  InteractiveMapBlock,
  MapBlockComponent,
} from '@/types/map/map.types'
import {
  MAP_ROUTE_ACTIVE_COLOR,
  MAP_ROUTE_INACTIVE_COLOR,
} from '@/utils/mapbox/constants'
import {
  calculateDynamicOffset,
  calculateMaxZoomBasedOnDistance,
  calculatePaddingByVariables,
  handleAntimeridianCrossingRoutes,
} from '@/utils/mapbox/coordinate-converters'

export const createMapChangeHandler =
  (
    alpineContext: MapBlockComponent<InteractiveMapBlock>,
    { destinations: providedDestinations }: any,
  ) =>
  (event: any) => {
    const destinations = structuredClone(providedDestinations)
    const nextDestinationIndex = event.detail.destinationIndex
    const shouldFocusOnBounds = event.detail.focusOnBounds

    if (shouldFocusOnBounds && alpineContext.bounds) {
      const bounds = alpineContext.bounds as [number, number][]

      alpineContext.focusOnMap(
        { latitude: bounds[0][1], longitude: bounds[0][0] },
        { latitude: bounds[1][1], longitude: bounds[1][0] },
      )

      alpineContext.setActiveRoute(-1)

      return
    }

    alpineContext.activeDestinationIndex = nextDestinationIndex
    const nextFocusDestination = destinations[nextDestinationIndex] || null
    const previousFocusDestination =
      nextDestinationIndex > 0 && destinations[nextDestinationIndex - 1]

    if (nextFocusDestination) {
      alpineContext.focusOnMap(
        nextFocusDestination,
        previousFocusDestination ? previousFocusDestination : undefined,
      )
    }

    if (nextDestinationIndex > 0) {
      alpineContext.setActiveRoute(nextDestinationIndex)
    }
  }

export const interactiveMap = ({
  camera,
  destinations,
  routes,
  hideMapStats,
  isInteractiveMap,
  options,
}: MapComponentConfig): MapBlockComponent<InteractiveMapBlock> => ({
  ...baseMapContextState({ hideMapStats, options }),
  activeDestinationIndex: 0,
  isInteractiveMap,
  totalDestinations: destinations.length,

  // eslint-disable-next-line no-unused-vars
  mapChangeHandler: (_event: any): void => {},

  init() {
    baseMapInit(this as InteractiveMapBlock)

    const mapChangeHandler = createMapChangeHandler(
      this as InteractiveMapBlock,
      { destinations },
    )

    window.addEventListener('map:change', mapChangeHandler)

    this.mapChangeHandler = mapChangeHandler
  },

  destroy() {
    window.removeEventListener('map:change', this.mapChangeHandler)
    baseMapDestroy(this as InteractiveMapBlock)
  },

  async initMapbox() {
    await baseInitMapbox(this as InteractiveMapBlock, {
      camera,
      destinations,
      routes,
      isInteractiveMap,
    })

    const bounds = this.bounds as [number, number][]

    bounds.length > 1 &&
      this.focusOnMap(
        { latitude: bounds[0][1], longitude: bounds[0][0] },
        { latitude: bounds[1][1], longitude: bounds[1][0] },
      )
  },

  destroyMapbox() {
    baseDestroyMapbox(this as InteractiveMapBlock)
  },

  focusOnMap(currentRoute, previouseRoute) {
    if (!currentRoute) return

    if (!previouseRoute) {
      return this.flyToRoute(currentRoute)
    }

    this.focusOnTwoRouteBounds(previouseRoute, currentRoute)
  },

  flyToRoute(route) {
    return this.mapbox?.flyTo({
      center: [route.longitude, route.latitude],
      zoom: 12,
      essential: true,
      speed: 1.2,
      curve: 1,
      easing: function (t) {
        return t
      },
    })
  },

  focusOnTwoRouteBounds(firstRoute, secondRoute) {
    try {
      const [parsedFirstRoute, parsedSecondRoute] =
        handleAntimeridianCrossingRoutes(firstRoute, secondRoute)

      this.mapbox?.fitBounds(
        [
          [parsedFirstRoute.longitude, parsedFirstRoute.latitude],
          [parsedSecondRoute.longitude, parsedSecondRoute.latitude],
        ],
        {
          padding: calculatePaddingByVariables(
            firstRoute,
            secondRoute,
            this.isMobile ?? false,
          ),
          offset: this.isMobile
            ? [0, 0]
            : calculateDynamicOffset(firstRoute, secondRoute),
          maxZoom: calculateMaxZoomBasedOnDistance(firstRoute, secondRoute),
          essential: true,
          speed: 0.6,
          curve: 1.2,
          animate: true,
        },
      )
    } catch (error) {
      console.log('error', error)
    }
  },

  setActiveRoute(activeDestinationIndex: number) {
    if (!this.mapbox) return

    const updateRouteLineColor = (routeId: number, activeRouteId: number) => {
      if (!this.mapbox?.getSource(`route-source-${routeId}`)) return

      const color =
        routeId === activeRouteId
          ? MAP_ROUTE_ACTIVE_COLOR
          : MAP_ROUTE_INACTIVE_COLOR

      this.mapbox?.setPaintProperty(
        `route-layer-${routeId}`,
        'line-color',
        color,
      )
    }

    const activeRouteId = activeDestinationIndex - 1
    // Generate a list of route ids from 0 to totalDestinations
    const allRouteIds = Array.from(Array(this.totalDestinations).keys())

    allRouteIds.forEach((routeId) => {
      updateRouteLineColor(routeId, activeRouteId)
    })
  },
})
